/**
 * Member home-location picker (DNKADM-0239; DNKAPI-0329) — the dialog the ONE write
 * path (`useHomeLocationChange`, in `./homeLocationChange`) drives.
 *
 * The card this module shipped as was retired in DNKADM-0247: the member's
 * **effective home location** (the stored home, or the server's single-active-location
 * fallback) now reads in the member-detail header, and **Set / Change home location**
 * is an item in the header's overflow menu. DNKADM-0249 moved the non-component
 * exports (`homeName`, `useHomeLocationChange`) out to the sibling module, leaving this
 * file the picker component only.
 *
 * What the field drives is stated **in the dialog**, at the point of the decision: the
 * home location determines which location's tax rates apply to this member's purchases,
 * and dink-api rejects a one-time sale before it charges when there is no effective home
 * (`ensureHomeForPurchase`, dink-api 2026-08-04) — so an unset home BLOCKS a purchase
 * rather than merely leaving it untaxed (DNKADM-0249 corrected the earlier "tax
 * calculated at checkout" framing). An unset home is therefore an actionable defect,
 * marked in the header with a warning-toned **"Not set"** chip, never a neutral blank
 * (which would read as "no locations exist"). The rule is the SERVER's: nothing here
 * pre-blocks a sale.
 *
 * **Fail-closed picker** (mirrors `CoachAssignmentControl`): a location-list load
 * failure shows error + Retry, never a partial/empty picker. Only **active**
 * locations are offered (the server would 400 an inactive target anyway; there is
 * no pre-validation beyond "a location is selected").
 *
 * Not admin-gated, and deliberately so: changing a home location is available to
 * every operator who can view the member (only the GDPR "Delete member" beside it
 * in the same menu is admin-only — DNKADM-0247).
 */
import { useCallback } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { PickListLoader } from '@/components/PickListLoader';
import { usePickList } from '@/hooks/usePickList';
import { PickListError } from '@/api/picklists';
import { listLocations } from '@/api/locations';
import { Icon, resolveTokens } from '@/theme';
import type { HomeLocationOption } from './homeLocationChange';

/**
 * Load the tenant's ACTIVE locations as fail-closed picker options (no shared
 * location pick-list exists — this local wrapper mirrors the others' fail-closed
 * contract, like the services feature's `getLocationOptions`). The active-only
 * filter is both requested (`status=active`) and applied client-side.
 */
async function getActiveLocationOptions(): Promise<HomeLocationOption[]> {
  try {
    const locations = await listLocations({ status: 'active' });
    return locations
      .filter((l) => l.isActive)
      .map((l) => ({ id: l.id, name: l.name.length > 0 ? l.name : l.id }));
  } catch (cause) {
    throw new PickListError('locations', cause);
  }
}

/**
 * The fail-closed home-location picker dialog — remounted per open so the active
 * location list reloads LIVE (never cached). A load failure shows error + Retry,
 * never a partial/empty picker. A failed write's inline error renders here, above
 * the list, so the operator sees it at the point of the action — as does the
 * what-it-drives copy (DNKADM-0247), which moved here from the retired card.
 */
export function HomeLocationPickerDialog({
  currentLocationId,
  busy,
  error,
  onClose,
  onPick,
}: {
  currentLocationId: string | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onPick: (option: HomeLocationOption) => void;
}) {
  // Live, fail-closed active-locations pick-list — reloads on each open (each mount).
  const locations = usePickList(
    useCallback(() => getActiveLocationOptions(), []),
    [],
  );
  const theme = useTheme();
  const t = resolveTokens(theme);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="home-location-picker-title"
    >
      <DialogTitle
        id="home-location-picker-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}
      >
        <Avatar
          variant="rounded"
          sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}
        >
          <Icon name="location_on" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Set home location</Typography>
          <Typography variant="body2">
            Pick the location whose tax rates apply to this member.
          </Typography>
        </Box>
        <IconAction size="small" onClick={onClose} label="Close" disabled={busy}>
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" role="alert" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        {/* What the field drives, at the point of the decision (U2, relocated here from the
            retired card by DNKADM-0247). DNKADM-0249 corrected the consequence: dink-api
            rejects a one-time sale before it charges without an effective home
            (`ensureHomeForPurchase`), so the purchase is BLOCKED, not merely untaxed. */}
        <Typography variant="body2" sx={{ mb: 1.5, color: t.text2 }}>
          The home location determines which location’s tax rates apply to this member’s
          purchases — until one is set, a purchase can’t be completed for this member.
          Changing it does not re-tax an already-active subscription.
        </Typography>
        <PickListLoader list={locations} label="the location list">
          {(options) =>
            options.length === 0 ? (
              <Typography variant="body2" sx={{ color: t.text2, py: 1 }}>
                No active locations are available to set.
              </Typography>
            ) : (
              <List disablePadding>
                {options.map((option) => {
                  const isCurrent = option.id === currentLocationId;
                  return (
                    <ListItemButton
                      key={option.id}
                      onClick={() => onPick(option)}
                      disabled={busy || isCurrent}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: t.brandSoft, color: t.brand }}>
                          <Icon name="location_on" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={isCurrent ? `${option.name} (current)` : option.name}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )
          }
        </PickListLoader>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={onClose} disabled={busy} sx={{ color: t.text2 }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
